'use client';

import { Box, Grid, VStack } from '@terra/ui-styled-system';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ChatExpansionProvider } from './expansion/ChatExpansionProvider';
import { TerminalApprovalRulesProvider } from './components/HITL/terminal-approval-rules-context';
import {
  GeneratingIndicator,
  useGeneratingIndicatorPresence,
} from './components/GeneratingIndicator';
import { InlineFeedbackForm } from './feedback/InlineFeedbackForm';
import { CopilotComposer, EditorComponent } from './editor/lexical';
import { useChatComposerEditor } from './hooks/useChatComposerEditor';
import { useTranslations } from 'next-intl';
import { ChatErrorMessage } from './components/ChatErrorMessage';
import { ChatNewMessagesIndicator } from './components/ChatNewMessagesIndicator';
import type { MessageListCallbacks } from './components/ChatMessageList';
import {
  VirtualizedChatMessageList,
  type VirtualizedChatMessageListRef,
} from './components/VirtualizedChatMessageList';
import { useProcessedMessages } from './hooks/useProcessedMessages';
import {
  getLastAssistantMessage,
  hasPendingToolResult,
  shouldShowChatGeneratingIndicator,
} from './utils/chat-generating-indicator.utils';
import {
  scrollableStyles,
  scrollableStylesFlushTop,
} from './chat-scroll-container.styles';
import type { ChatProps, ChatRef } from './Chat.types';

export type { ChatProps, ChatRef } from './Chat.types';

export const Chat = forwardRef<ChatRef, ChatProps>(function Chat(
  {
    messages,
    isLoading,
    streamIsLoading,
    hasActiveNeoAttackTracking,
    neoAttackTrackingIdentity,
    onSend,
    agent,
    user,
    onDataMessageEvent,
    onHITLActionMessageEvent,
    onRetry,
    onAbort,
    onAbortSubagent,
    focusedSubagentToolCallId,
    error,
    onMentionsTrigger,
    onFileUploadClick,
    showFileUpload,
    noAssistantHeader,
    onSSESubscribe,
    footerLeftContent,
    footerRightContent,
    composerTopContent,
    composerAttachments,
    statusLineContent,
    slashCommands,
    onCycleMode,
    isSubmitDisabled,
    allowEmptySubmit = false,
    suppressPendingHITLLoading,
    generatingIndicatorLoading,
    isReconnecting = false,
    isCompacting = false,
    compactionSummaryText = null,
    isFeedbackEnabled,
    onOpenEditInMainContent,
    onCloseEditInMainContent,
    onOpenHttpInMainContent,
    onOpenNodeArgsInMainContent,
    onOpenBrowserStreamInMainContent,
    onOpenPlanInMainContent,
    onBuildPlan,
    onRevertCopilotModeSwitch,
    onConfirmCopilotModeSwitch,
    currentCopilotMode,
    pinnedHeaderContent,
    hideInlinePlanAndTodos,
    onOpenObject,
    onTerminalAlwaysAllow,
    skillDraftReference,
    emptyState,
    onLoadMore,
    hasMoreMessages,
    isLoadingMoreMessages,
    instanceId,
    isActive = true,
    messageHistorySessionId,
    onEditUserMessage,
    onEditAskUserAnswer,
    onInputChange,
    messagePlaceholder,
    sendButtonText,
    composerVariant = 'default',
    inlineEdit,
    checkpoint,
  },
  ref,
) {
  const t = useTranslations('designSystem.chat.chat');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { processedMessages, stableIsLoading: computedStableIsLoading } =
    useProcessedMessages({
      messages,
      isLoading,
      streamIsLoading,
      hasActiveNeoAttackTracking,
      neoAttackTrackingIdentity,
      hideInlinePlanAndTodos,
    });
  const stableIsLoading = suppressPendingHITLLoading
    ? isLoading
    : computedStableIsLoading;

  const lastAssistantMessage = getLastAssistantMessage(messages);
  const effectiveIsSubmitDisabled = isSubmitDisabled ?? stableIsLoading;
  const isWaitingForToolResult = hasPendingToolResult(processedMessages);
  const generatingIndicatorIsLoading = generatingIndicatorLoading ?? isLoading;
  const shouldShowGeneratingIndicator = shouldShowChatGeneratingIndicator({
    isLoading: generatingIndicatorIsLoading,
    isWaitingForToolResult,
    lastAssistantMessage,
    isReconnecting,
    isCompacting,
  });
  const generatingIndicatorPresence = useGeneratingIndicatorPresence({
    shouldShow: shouldShowGeneratingIndicator,
    isReconnecting,
  });

  const {
    input,
    setInput,
    editorRef,
    editorRefCallback,
    inlineEditorRefCallback,
    handleMessageHistoryEntryRestored,
  } = useChatComposerEditor({ instanceId, isActive, onInputChange });
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizedListRef = useRef<VirtualizedChatMessageListRef>(null);

  useImperativeHandle(
    ref,
    () => ({
      setInput: (value: string) => {
        setInput(value);
        editorRef.current?.setContent(value);
      },
      setContentFromMessageSegments: ({ segments, mentions }) => {
        editorRef.current?.setContentFromMessageSegments({
          segments,
          mentions,
        });
        requestAnimationFrame(() => {
          setInput(editorRef.current?.getContent() ?? '');
        });
      },
      appendText: (value: string) => {
        editorRef.current?.appendText(value);
      },
      clearInput: () => {
        setInput('');
        editorRef.current?.clearContent();
      },
      getInput: () => {
        if (editorRef.current) {
          return editorRef.current.getContent();
        }

        return input;
      },
      setErrorMessage: (message: string | null) => {
        setErrorMessage(message);
      },
      focus: () => {
        editorRef.current?.focus();
      },
      getMentionsAnchorPosition: () => {
        return editorRef.current?.getMentionsAnchorPosition();
      },
      scrollToBottom: (_instant?: boolean) => {
        virtualizedListRef.current?.scrollToEnd();
      },
      scrollToToolCallId: (toolCallId: string) => {
        return (
          virtualizedListRef.current?.scrollToToolCallId(toolCallId) ?? false
        );
      },
    }),
    [editorRef, input, setInput],
  );

  const messageCallbacks = useMemo<MessageListCallbacks>(
    () => ({
      onDataMessageEvent,
      onHITLActionMessageEvent,
      onAbortSubagent,
      focusedSubagentToolCallId,
      onOpenEditInMainContent,
      onCloseEditInMainContent,
      onOpenHttpInMainContent,
      onOpenNodeArgsInMainContent,
      onOpenBrowserStreamInMainContent,
      onOpenPlanInMainContent,
      onBuildPlan,
      onRevertCopilotModeSwitch,
      onConfirmCopilotModeSwitch,
      currentCopilotMode,
      onOpenObject,
      skillDraftReference,
      onSSESubscribe,
      onEditUserMessage,
      onEditAskUserAnswer,
      inlineEdit: inlineEdit
        ? {
            ...inlineEdit,
            editorRef: inlineEditorRefCallback,
            onMentionsTrigger,
          }
        : undefined,
      checkpoint,
    }),
    [
      onDataMessageEvent,
      onHITLActionMessageEvent,
      onAbortSubagent,
      focusedSubagentToolCallId,
      onOpenEditInMainContent,
      onCloseEditInMainContent,
      onOpenHttpInMainContent,
      onOpenNodeArgsInMainContent,
      onOpenBrowserStreamInMainContent,
      onOpenPlanInMainContent,
      onBuildPlan,
      onRevertCopilotModeSwitch,
      onConfirmCopilotModeSwitch,
      currentCopilotMode,
      onOpenObject,
      skillDraftReference,
      onSSESubscribe,
      onEditUserMessage,
      onEditAskUserAnswer,
      inlineEdit,
      inlineEditorRefCallback,
      onMentionsTrigger,
      checkpoint,
    ],
  );

  const composerProps = {
    ref: editorRefCallback,
    onMentionsTrigger,
    isLoading: stableIsLoading,
    onSubmit: (message: { content: string }) => {
      onSend({
        id: uuidv4(),
        role: 'user' as const,
        parts: [{ type: 'text' as const, text: message.content }],
      });
    },
    onStop: () => {
      onAbort?.();
    },
    onCycleMode,
    slashCommands,
    statusLineContent,
    isSubmitDisabled: effectiveIsSubmitDisabled,
    allowEmptySubmit,
    submitButtonAriaLabel:
      stableIsLoading && isSubmitDisabled === false
        ? t('queueMessage')
        : sendButtonText,
    placeholder: messagePlaceholder,
    onFileUploadClick,
    showFileUpload,
    footerLeftContent,
    footerRightContent,
    composerAttachments,
    messageHistorySessionId,
    onMessageHistoryEntryRestored: handleMessageHistoryEntryRestored,
  };

  const handleJumpToLatest = useCallback(() => {
    virtualizedListRef.current?.scrollToEnd();
  }, []);

  return (
    <ChatExpansionProvider>
      <TerminalApprovalRulesProvider onAlwaysAllow={onTerminalAlwaysAllow}>
        <Grid
          w="full"
          h="full"
          minH={0}
          gridTemplateRows="minmax(0, 1fr) auto"
          overflow="hidden"
          style={{ containerType: 'size' }}
          bg="bg.default"
          px={composerVariant === 'copilot' ? 3 : 1}
          pt={pinnedHeaderContent ? 0 : 1}
          pb={composerVariant === 'copilot' ? 3 : 6}
          gap={0.5}
        >
          <Box
            ref={scrollRef}
            h="full"
            minH={0}
            bg="bg.default"
            className={
              pinnedHeaderContent ? scrollableStylesFlushTop : scrollableStyles
            }
            gap={0}
            willChange="scroll-position"
          >
            {processedMessages.length === 0 && emptyState}
            {processedMessages.length > 0 && (
              <VirtualizedChatMessageList
                ref={virtualizedListRef}
                processedMessages={processedMessages}
                stableIsLoading={stableIsLoading}
                isFeedbackEnabled={isFeedbackEnabled}
                agent={agent}
                user={user}
                noAssistantHeader={noAssistantHeader}
                callbacks={messageCallbacks}
                scrollElementRef={scrollRef}
                onLoadMore={onLoadMore}
                hasMoreMessages={hasMoreMessages}
                isLoadingMoreMessages={isLoadingMoreMessages}
                pinnedHeaderContent={pinnedHeaderContent}
                scrollSessionId={instanceId}
                isScrollerActive={isActive}
              />
            )}
            {isFeedbackEnabled && <InlineFeedbackForm />}
            {generatingIndicatorPresence.visible && (
              <GeneratingIndicator
                isReconnecting={generatingIndicatorPresence.showAsReconnecting}
                isExitingReconnecting={
                  generatingIndicatorPresence.isExitingReconnecting
                }
                isCompacting={isCompacting}
                compactionSummaryText={compactionSummaryText}
              />
            )}
            {error && !generatingIndicatorPresence.showAsReconnecting && (
              <ChatErrorMessage
                errorMessage={error.message || t('error')}
                onRetry={onRetry}
              />
            )}
            {errorMessage &&
              !generatingIndicatorPresence.showAsReconnecting && (
                <ChatErrorMessage
                  errorMessage={errorMessage}
                  onRetry={onRetry}
                />
              )}
          </Box>
          <VStack
            gap={0}
            alignItems="stretch"
            bg="bg.default"
            minW={0}
            minH={0}
            flexShrink={0}
            w="full"
          >
            <ChatNewMessagesIndicator
              messages={processedMessages}
              scrollElementRef={scrollRef}
              onJumpToLatest={handleJumpToLatest}
            />
            {composerTopContent && (
              <Box
                px={composerVariant === 'copilot' ? 0 : 3}
                pb={2}
                minW={0}
                w="full"
                position="relative"
                overflow={composerVariant === 'copilot' ? 'visible' : 'hidden'}
              >
                {composerTopContent}
              </Box>
            )}
            {composerVariant === 'copilot' ? (
              <CopilotComposer {...composerProps} />
            ) : (
              <EditorComponent {...composerProps} variant={composerVariant} />
            )}
          </VStack>
        </Grid>
      </TerminalApprovalRulesProvider>
    </ChatExpansionProvider>
  );
});
